import React, { useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ScrollView, Alert } from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useMyProducts } from '../hooks/useMyProducts';
import { ProductCard } from '../components/products/ProductCard';
import { Button } from '../components/ui/Button';
import { LoadingState } from '../components/ui/LoadingState';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { spacing } from '../theme';
import { useColors, ThemeColors } from '../hooks/useColors';
import VendorRegistrationScreen from './VendorRegistration';
import { StorefrontCustomization } from '../components/StorefrontCustomization';
import { ProductManagement } from '../components/ProductManagement';
import ProductListingScreen from './ProductListingScreen';

export default function SellScreen() {
  const colors = useColors();
  const styles = createStyles(colors);
  const navigation = useNavigation();
  const queryClient = useQueryClient();
  
  // Get current user ID for query key - this ensures cache is per-user
  const { data: sessionData, isLoading: isSessionLoading } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      console.log('[SellScreen] Fetching session...');
      const { data: { session } } = await supabase.auth.getSession();
      console.log('[SellScreen] Session fetched:', session?.user?.id || 'no user');
      return session;
    },
    staleTime: 5 * 1000, // Reduced to 5 seconds to ensure fresh session after sign-in
    refetchOnMount: 'always', // Always refetch when component mounts
  });
  const currentUserId = sessionData?.user?.id;
  
  // Check if user is a vendor
  // CRITICAL: Include userId in queryKey to prevent cross-user cache contamination
  const { data: vendor, isLoading } = useQuery({
    queryKey: ['vendor-check', currentUserId],
    enabled: !!currentUserId, // Don't run until we have a user ID
    queryFn: async () => {
      console.log('[SellScreen] Executing vendor-check query...');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        console.log('[SellScreen] No active user session found');
        return null;
      }

      console.log('[SellScreen] Fetching vendor data for user:', session.user.id);
      
      // First check if there are ANY active (non-deleted) vendors
      const { data: allVendors, error: checkError } = await supabase
        .from('vendors')
        .select('id, store_name, status, created_at, is_deleted')
        .eq('user_id', session.user.id)
        .neq('status', 'deleted')
        .not('is_deleted', 'eq', true);  // Exclude vendors where is_deleted = true
        
      if (allVendors && allVendors.length > 0) {
        console.log('[SellScreen] All vendors found:', allVendors);
      } else {
        console.log('[SellScreen] No vendors found for user');
      }
      
      // Get the most recent vendor for this user (excluding deleted vendors)
      // Check both is_deleted and status to ensure deleted vendors are filtered out
      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .eq('user_id', session.user.id)
        .neq('status', 'deleted')
        .not('is_deleted', 'eq', true)  // Exclude where is_deleted = true
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('[SellScreen] Error fetching vendor:', error);
        return null;
      }

      if (!data || data.length === 0) {
        console.log('[SellScreen] No vendor found for user:', session.user.id);
        return null;
      }

      // Get the most recent vendor record
      const mostRecentVendor = data[0];
      console.log("[SellScreen] Vendor found:", mostRecentVendor.id, "Status:", mostRecentVendor.status);
      return mostRecentVendor;
    },
    // Short stale time to ensure store creation is reflected quickly
    // Invalidation will trigger immediate refetch regardless
    staleTime: 5 * 1000,
    // Don't use placeholderData when vendor is null - we want to show loading/empty state
    // This ensures newly created vendors are shown immediately after refetch
    retry: 2,
    retryDelay: 1000,
  });

  const { data: products, isLoading: isLoadingProducts } = useMyProducts();

  // Refetch session and vendor-check when screen gains focus
  // This is needed because QueryClient has refetchOnWindowFocus: false
  // Also helps when signing in from guest mode - ensures fresh data
  useFocusEffect(
    useCallback(() => {
      // Always refetch session when screen gains focus
      queryClient.invalidateQueries({ queryKey: ['session'] });
      
      if (currentUserId) {
        queryClient.invalidateQueries({ queryKey: ['vendor-check', currentUserId] });
      }
    }, [currentUserId, queryClient])
  );

  // Show loading state while:
  // 1. Session is loading (we don't know the user ID yet)
  // 2. We have the user ID but vendor-check is still loading
  // This prevents flashing VendorRegistrationScreen before we know if user has a vendor
  if (isSessionLoading || !currentUserId || (isLoading && vendor === undefined)) {
    return <LoadingState />;
  }

  return vendor ? <ProductListingScreen /> : <VendorRegistrationScreen />;
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  content: {
    padding: spacing.lg,
  },
  section: {
    marginTop: spacing.lg,
  },
  header: {
    padding: spacing.md,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: colors.text,
  },
  emptyState: {
    padding: spacing.xl,
    alignItems: 'center',
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.text,
    marginBottom: spacing.sm,
  },
  emptyText: {
    fontSize: 16,
    color: colors.textLight,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  addButton: {
    width: '100%',
    maxWidth: 200,
  },
  productsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
    padding: spacing.lg,
  },
  productCard: {
    width: '48.5%',
  },
}); 